import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { APP_NAME, PRIVACY_EMAIL } from "@/lib/constants";
import { SEO } from "@/components/SEO";

const Privacy = () => {
  return (
    <Layout>
      <SEO 
        title="מדיניות פרטיות" 
        description={`מדיניות הפרטיות של ${APP_NAME} - גלו כיצד אנו אוספים, שומרים ומגינים על המידע שלכם.`} 
      />
      <div className="bg-white min-h-screen" dir="rtl">
        <div className="border-b border-slate-100 pt-32 pb-10">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <Link to="/" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-8 font-medium transition-colors">
              <ArrowRight className="h-4 w-4 ms-2" />
              חזרה לדף הבית
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">מדיניות פרטיות</h1>
            <p className="text-slate-500 mt-3">עדכון אחרון: פברואר 2026</p>
          </div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 max-w-3xl py-12">
          <div className="prose prose-slate max-w-none prose-headings:tracking-tight prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-p:leading-relaxed prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">

            <p className="text-lg text-slate-700 font-medium">
              ב-{APP_NAME} אנו מייחסים חשיבות עליונה לפרטיות המשתמשים שלנו. 
              מדיניות זו מפרטת כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלך.
            </p>

            <h2>1. מידע שאנו אוספים</h2>

            <h3>1.1 מידע שאתה מספק לנו</h3>
            <ul>
              <li><strong>פרטי הרשמה:</strong> שם מלא, כתובת דוא"ל, סיסמה (מוצפנת).</li>
              <li><strong>פרטי מערכות:</strong> שם, תיאור, תמונות, קישורים — עבור יוצרים ומפרסמים.</li>
              <li><strong>ביקורות ודירוגים:</strong> תוכן חוות דעת, דירוג כוכבים.</li>
            </ul>

            <h3>1.2 מידע שנאסף אוטומטית</h3>
            <ul>
              <li><strong>נתוני שימוש:</strong> צפיות בעמודים, אינטראקציות עם מערכות, זמני ביקור.</li>
              <li><strong>מידע טכני:</strong> סוג דפדפן, מערכת הפעלה, כתובת IP (לצורכי אבטחה).</li>
              <li><strong>מזהה סשן אנונימי:</strong> לצורך ספירת צפיות ומניעת כפילויות.</li>
            </ul>

            <h2>2. כיצד אנו משתמשים במידע</h2>
            <ul>
              <li>הפעלת הפלטפורמה ומתן השירותים (הרשמה, פרסום, דירוג).</li>
              <li>שליחת התראות על פעילות הקשורה לחשבונך (אישור מערכת, ביקורת חדשה, מועדף חדש).</li>
              <li>הצגת אנליטיקס ליוצרים (צפיות, דירוגים — ללא חשיפת פרטי גולשים).</li>
              <li>שיפור ופיתוח הפלטפורמה על בסיס ניתוח שימוש אגרגטיבי.</li>
              <li>אבטחת הפלטפורמה וזיהוי שימוש לרעה.</li>
            </ul>

            <h2>3. שיתוף מידע עם צדדים שלישיים</h2>
            <p>אנו <strong>לא מוכרים ולא משתפים</strong> מידע אישי עם צדדים שלישיים, למעט:</p>
            <ul>
              <li><strong>ספקי שירות:</strong> Supabase (אחסון נתונים ואימות), שרתי CDN להגשת תוכן.</li>
              <li><strong>דרישה חוקית:</strong> אם נדרש על פי צו בית משפט או דרישה חוקית מחייבת.</li>
              <li><strong>הגנה על זכויות:</strong> למניעת הונאה, שימוש לרעה או איום על אבטחת המשתמשים.</li>
            </ul>

            <h2>4. אבטחת מידע</h2>
            <p>אנו נוקטים באמצעים סבירים להגנה על המידע שלך:</p>
            <ul>
              <li>סיסמאות מוצפנות באמצעות hashing חד-כיווני (bcrypt).</li>
              <li>תקשורת מוצפנת בפרוטוקול HTTPS.</li>
              <li>הרשאות גישה מבוססות תפקידים (RLS) ברמת מסד הנתונים.</li>
              <li>מפתחות API מופרדים — מפתח הצד-שרת לא נחשף לצד הלקוח.</li>
              <li>מנגנון חסימת משתמשים עם ביטול מיידי של סשנים.</li>
            </ul>

            <h2>5. Cookies ומעקב</h2>
            <p>
              הפלטפורמה משתמשת ב-Local Storage וב-Session Storage לצורכים הבאים בלבד:
            </p>
            <ul>
              <li>שמירת סשן התחברות (Token).</li>
              <li>מזהה אנונימי למניעת ספירת צפיות כפולות.</li>
              <li>העדפות ממשק (כגון מצב כהה, אם קיים).</li>
            </ul>
            <p>
              אנו <strong>לא משתמשים</strong> ב-Cookies של צדדים שלישיים לצורכי מעקב או פרסום.
            </p>

            <h2>6. זכויות המשתמש</h2>
            <p>בהתאם לחוק הגנת הפרטיות הישראלי, עומדות לך הזכויות הבאות:</p>
            <ul>
              <li><strong>עיון:</strong> הזכות לעיין במידע האישי השמור אודותיך.</li>
              <li><strong>תיקון:</strong> הזכות לתקן מידע שגוי או לא מדויק.</li>
              <li><strong>מחיקה:</strong> הזכות לבקש מחיקת החשבון וכל המידע הנלווה.</li>
              <li><strong>התנגדות:</strong> הזכות להתנגד לעיבוד מידע לצרכים שאינם חיוניים לשירות.</li>
            </ul>
            <p>
              לממוש זכויות אלו, ניתן לפנות אלינו בכתובת{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
            </p>

            <h2>7. שמירת מידע</h2>
            <ul>
              <li>מידע חשבון נשמר כל עוד החשבון פעיל.</li>
              <li>ביקורות נשמרות גם לאחר מחיקת חשבון (באנונימיזציה).</li>
              <li>לוגים טכניים נמחקים אוטומטית לאחר 90 יום.</li>
              <li>לאחר בקשת מחיקת חשבון, כל המידע האישי יימחק תוך 30 יום עסקים.</li>
            </ul>

            <h2>8. קטינים</h2>
            <p>
              הפלטפורמה מיועדת למשתמשים בני 18 ומעלה. אנו לא אוספים ביודעין מידע מקטינים. 
              אם נודע לנו על חשבון של קטין, הוא ייחסם ויימחק.
            </p>

            <h2>9. שינויים במדיניות</h2>
            <p>
              אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים מהותיים יפורסמו באתר 
              ויישלחו כהתראה למשתמשים רשומים. תאריך העדכון האחרון מופיע בראש הדף.
            </p>

            <h2>10. יצירת קשר</h2>
            <p>לשאלות בנושא פרטיות, ניתן לפנות:</p>
            <ul>
              <li>דוא"ל: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a></li>
              <li>דרך הגדרות החשבון בפלטפורמה</li>
            </ul>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;